// Dependencies
import React from 'react'
import ListsContainer from './ListsContainer'
import Portal from '../Portal'
import { CSSTransition } from 'react-transition-group'
import styled, {createGlobalStyle} from 'styled-components'
import { MobileMenuContext } from '../../contexts/MobileMenu'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Menu from './Menu'


// Types
import { Types, Components } from '../../types'

// Hooks
import { useDebug } from '../../hooks'
import useAnimatedRender from '../../hooks/useAnimatedRender'
import { CSSTransitionProps } from 'react-transition-group/CSSTransition'

// Components.MobileMenuPortal
const Sidebar = (props: any) => {
  useDebug('Components/MobileMenu/Sidebar')

  const { state, handleBeforeReset } = React.useContext(MobileMenuContext)
  const shouldRender = useAnimatedRender(state.expanded && ! state.collapsing);
  const sidebarRef = React.useRef<any>(null);

  const transitionProps: CSSTransitionProps = React.useMemo(() => ({
    timeout: props.config.general.timingBase,
    unmountOnExit: true
  }), [props.config.general.timingBase])

  return (
    <CSSTransition {...transitionProps} in={state.expanded}>
      <Portal>
        <CSSTransition {...transitionProps} in={shouldRender} classNames="sidebar">
          <StyledSidebar {...props} ref={sidebarRef}>
            <Menu {...props} boot />
          </StyledSidebar>
        </CSSTransition>
        <CSSTransition {...transitionProps} in={shouldRender} classNames="overlay">
          <StyledOverlay {...props} onClick={() => handleBeforeReset(props.config.general.timingBase)} />
        </CSSTransition>
      </Portal>
    </CSSTransition>
  )
}

// Menus container relatively positioned to the fixed Portal
const StyledSidebar = styled.div<Types.MegamenuOptions>`
  ${({ config: c, theme: t }) => `
  position: fixed;
  top: 0;
  bottom: 0;
  ${c.mobileMenu.inverse ? 'left' : 'right'}: 0;
	z-index: 999999999;
  width: 100%; 
  height: 100%;
  font-size: ${c.general.sizingBase};
	max-width: ${`${c.mobileMenu.maxWidth}px`};
  font-family: ${t.general.cssFont};
  font-size: ${c.general.sizingBase}px;
  color: ${t.mobileMenu.textColor};

  &.sidebar-enter {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
  }

  &.sidebar-enter-active {
   transform: translate(0, 0);
    ${
      !c.general.turnOffTransitions
        ? `transition: transform ${c.general.timingBase}ms ${c.general.transitionFunction};`
        : ''
    }
  }

  &.sidebar-enter-done {
    transform: translate(0, 0);
  }

  &.sidebar-exit-enter {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
  }

  &.sidebar-exit-active {
    transform: translate(${c.mobileMenu.inverse ? '-100%, 0' : '100%, 0'});
    ${
      !c.general.turnOffTransitions
        ? `transition: transform ${c.general.timingBase}ms ${c.general.transitionFunction};`
        : ''
    }
  }

  `}
`

// Overlay with fixed fullscreen layout
const StyledOverlay = styled.div<Types.MegamenuOptions>`
  ${({ config: c, theme: t }) => `
  position: fixed;
  inset: 0;
	z-index: 99999999;
  width: 100%;
  height: 100vh;
  background: ${t.general.overlayBackground};

  &.overlay-enter {
    opacity: 0;
  }

  &.overlay-enter-active {
    opacity: 1;
    ${!c.general.turnOffTransitions
      ? `transition: opacity ${c.general.timingBase}ms ${c.general.transitionFunction}`
      : ''
    };
  }

  &.overlay-exit-active {
    opacity: 0;
    ${!c.general.turnOffTransitions
      ? `transition: opacity ${c.general.timingBase}ms ${c.general.transitionFunction}`
      : ''
    };
  }
  `}
`

export default Sidebar;
